/**
 * Live-formats a phone input to the US mask `(XXX) XXX-XXXX`.
 *
 * Strips every non-digit character, caps at 10 digits, and formats
 * progressively so partial input reads naturally as the user types.
 */
export const formatUsPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10);

    if (digits.length === 0) {
        return "";
    }
    if (digits.length < 4) {
        return `(${digits}`;
    }
    if (digits.length < 7) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * A stored phone, as a form prefills it (the profile, the admin's Edit
 * Organization).
 *
 * Legacy rows predate the /apply mask — admin CreateUser and the seed data both
 * write raw digits — and an unmasked prefill fails the mask rule, which would
 * block *every* save, a pure name edit included, until the phone was retyped. A
 * stored value of exactly 10 digits is unambiguous, so it is masked. Anything
 * else is left exactly as stored: formatUsPhone strips non-digits and caps at
 * 10, so blanket-formatting "+1 555 123 4567" would silently rewrite it to
 * "(155) 512-3456" — a different number that could be saved without anyone
 * noticing. Better to show the real stored value and the validation message.
 */
export const prefillUsPhone = (stored: string | undefined): string => {
    if (!stored) {
        return "";
    }

    const digits = stored.replace(/\D/g, "");

    return digits.length === 10 ? formatUsPhone(digits) : stored;
};
